import React, { useId } from 'react';
import styled from 'styled-components';
import { Spacing4 } from 'autogen/design-tokens/tokens';
import { Button } from 'components/Button';
import type IButton from 'components/Button/shared/types/button';
import useDropdown from 'components/dropdown/hooks/useDropdown';
import { Typography } from 'components/generic/typography';
import Icon from 'components/Icon';
import Flex from 'components/Layout/components/Flex';
import HelperText from '../../components/HelperText';
import type { BaseInputProps } from '../../shared/types';
import { SelectButtonStyling } from '../shared/styles';

const SelectButtonElement = styled(Button)<{
    $isInvalid: boolean;
    isOpen: boolean;
    placement: string;
    disabled: boolean;
}>`
    ${SelectButtonStyling}
`;

const ClickableContainer = styled(Flex.Column)`
    cursor: pointer;
`;

const IconContainer = styled.div<{ left?: boolean; right?: boolean; $disabled?: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 16px;
    height: 16px;
    z-index: 1;
`;

const Arrow = styled(Icon)<{ $isOpen: boolean }>`
    min-width: 16px;
    min-height: 16px;
    margin-left: 8px;
    transition: transform 100ms ease;
    transform: ${({ $isOpen }): string => ($isOpen ? 'rotate(180deg)' : ' rotate(0deg)')};
`;

type SelectButtonProps = BaseInputProps & {
    leftIcon?: JSX.Element;
} & Omit<IButton, 'variant' | 'onClick'>;

const SelectButton = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, SelectButtonProps>((props, ref) => {
    const { children, size, isInvalid, className, label, leftIcon, disabled, helperText, ...restProps } = props;
    const { isVisible, toggle, placement, setMinWidth, show } = useDropdown();
    const helperTextId = useId();

    // https://reactjs.org/docs/refs-and-the-dom.html#callback-refs
    const attachRef = (node: HTMLAnchorElement | HTMLButtonElement) => {
        if (typeof ref === 'function') {
            ref(node); // handle function refs
        } else if (ref) {
            ref.current = node; // handle object refs
        }

        if (node) {
            setMinWidth(node.offsetWidth);
        }
    };

    const baseSelectButton = (
        <SelectButtonElement
            ref={attachRef}
            size={size}
            className={className}
            $isInvalid={isInvalid}
            variant={null}
            type="button"
            disabled={disabled}
            onClick={toggle}
            placement={placement}
            isOpen={isVisible}
            {...restProps}
        >
            {leftIcon && (
                <IconContainer $disabled={disabled} left>
                    {leftIcon}
                </IconContainer>
            )}
            <Typography.Label nowrap size="M">
                {children}
            </Typography.Label>
            <IconContainer $disabled={disabled} right>
                <Arrow type="ChevronDown" size={16} $isOpen={isVisible} />
            </IconContainer>
        </SelectButtonElement>
    );

    const selectButtonWithHelperText = helperText ? (
        <Flex.Column gap={Spacing4}>
            {baseSelectButton}
            <div>
                <HelperText helperText={helperText} helperTextId={helperTextId} />
            </div>
        </Flex.Column>
    ) : (
        baseSelectButton
    );

    if (label) {
        return (
            <ClickableContainer gap={Spacing4}>
                <Typography.Label strong size="S" onClick={show}>
                    {label}
                </Typography.Label>
                {selectButtonWithHelperText}
            </ClickableContainer>
        );
    }

    return selectButtonWithHelperText;
});

export default SelectButton;
